"use client";

import { Card, CardContent } from "@/components/rac/card";
import { type NearbyProperty } from "@/types/sup";
import { cn } from "@/utils/cn";
import currencify from "@/utils/currency/format";
import { s3Url } from "@/utils/s3-prefix";
import { toCdn } from "@/utils/to-cdn";
import { MapPin } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import React, { useCallback } from "react";
import { DistanceTooltip } from "../deal-dashboard/distance-tooltip";

interface ProjectCardProps {
	data: NearbyProperty;
	className?: string;
	distanceFromText?: string;
	shouldPrioritizeCoverImage?: boolean; // Control whether to prioritize the first (cover) image
	shouldRedirect?: boolean; // Control whether to redirect to the project page
}

const formatLocation = (locality: string): string => {
	if (!locality) return "Location Not Specified";
	return locality
		.split("-")
		.filter(Boolean)
		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ");
};

const formatRange = (min: number, max: number, formatter?: (value: number) => string): string => {
	if (min === max) return formatter ? formatter(min) : min.toString();
	return formatter ? `${formatter(min)} - ${formatter(max)}` : `${min} - ${max}`;
};

export const ProjectCard = React.memo(function ProjectCard({
	data,
	className,
	distanceFromText,
	shouldPrioritizeCoverImage = false,
	shouldRedirect,
}: ProjectCardProps) {
	const locality = data.localityName || "";
	const formattedLocation = formatLocation(locality);
	const name = data.projectName;

	let coverImage = data.cover;
	if (coverImage) {
		const rawImage = coverImage.startsWith("http")
			? coverImage
			: data.type === "unit" || coverImage.startsWith("units/")
				? s3Url(coverImage)
				: s3Url(`projects/${data.projectId}/images/${coverImage}`);
		coverImage = toCdn(rawImage);
	}

	const handleCardClick = useCallback(
		(e: React.MouseEvent) => {
			// Handle redirect if needed
			if (shouldRedirect) return;
			else e.preventDefault();
		},
		[shouldRedirect],
	);

	return (
		<Link
			href={`/units/${data.slug ? `${data.slug}-` : ""}${data._id}`}
			target="_blank"
			className={cn("h-full block", className)}
			onClick={handleCardClick}
		>
			<Card className="bg-white/80 hover:bg-white h-full transition-all duration-300 group cursor-pointer p-0 gap-3 shadow-none">
				<div className="relative aspect-[4/3] w-full bg-muted/20 overflow-hidden">
					<Image
						src={coverImage || "/images/home/hot-projects/project-generic.svg"}
						alt={name || "Project Cover Image"}
						fill
						className="object-cover transition-transform duration-500 group-hover:scale-105"
						sizes="(max-width: 768px) 100dvw, (max-width: 1200px) 50dvw, 33dvw"
						loading="lazy"
						priority={shouldPrioritizeCoverImage}
					/>
					{/* Project name overlay stays static */}
					<div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
					<div className="absolute bottom-0 left-0 right-0 p-2">
						<h3 className="font-semibold text-lg text-white line-clamp-2">{name}</h3>
						<div className="flex items-center text-sm text-white/90">
							<MapPin className="h-3.5 w-3.5 mr-1.5 flex-shrink-0" />
							<span className="truncate">{formattedLocation}</span>
							{data.distance && <DistanceTooltip distance={data.distance} distanceFromText={distanceFromText} />}
						</div>
					</div>
				</div>

				<CardContent className="px-2 pb-2">
					<div className="space-y-2">
						{/* Price Range */}
						{(data.prices || []).length > 0 && (
							<div className="flex justify-between items-center">
								<span className="text-sm text-gray-600">Price</span>
								<span className="text-sm font-medium text-primary-700">
									{formatRange(
										Math.min(...data.prices!),
										Math.max(...data.prices!),
										value => "₹" + currencify({ amount: value, abbreviated: true }),
									)}
								</span>
							</div>
						)}

						{/* Bedroom Range */}
						{(data.bedrooms || []).length > 0 && (
							<div className="flex justify-between items-center">
								<span className="text-sm text-gray-600">Bedrooms</span>
								<span className="text-sm font-medium">
									{formatRange(Math.min(...data.bedrooms!), Math.max(...data.bedrooms!))} BHK
								</span>
							</div>
						)}

						{/* Built-up Area Range */}
						{(data.builtUpAreas || []).length > 0 && (
							<div className="flex justify-between items-center">
								<span className="text-sm text-gray-600">Built-up Area</span>
								<span className="text-sm font-medium">
									{formatRange(Math.min(...data.builtUpAreas!), Math.max(...data.builtUpAreas!), value =>
										value.toLocaleString(),
									)}{" "}
									sqft
								</span>
							</div>
						)}
					</div>
				</CardContent>
			</Card>
		</Link>
	);
});
